/* ============================================================
   ZNAKI · audio/engine.js
   Silnik dźwięku instalacji — cienka warstwa nad Web Audio API.

   Odpowiada za:
     · kontekst audio i odblokowanie go pierwszym gestem użytkownika
     · wczytywanie i cache'owanie próbek (AudioBuffer)
     · magistrale grupowe (Bus) — głośność, panorama i filtr dla
       całej grupy dźwięków naraz
     · tworzenie pojedynczych głosów (Voice)

   Celowo bez biblioteki zewnętrznej: potrzebujemy filtra
   dolnoprzepustowego i próbkowo dokładnego planowania zakładek,
   czego Howler (użyty w edycjach 2023/2024) nie udostępnia.
   ============================================================ */

import AudioChain from './chain';
import Voice from './voice';

/* gesty, które w przeglądarkach liczą się jako aktywacja użytkownika;
   "wheel" bywa ignorowany, ale nic nie kosztuje spróbować */
const UNLOCK_EVENTS = ['pointerdown', 'touchend', 'keydown', 'wheel'];

class AudioEngine {

  /**
   * @param {object} opts — { volume }
   */
  constructor(opts = {}) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    this.supported = !!Ctx;
    if (!this.supported) return;

    this.ctx = new Ctx();
    this.master = new AudioChain(this.ctx, { volume: opts.volume !== undefined ? opts.volume : 1 });
    this.master.connect(this.ctx.destination);

    this.buses = new Map();
    this._buffers = new Map();     /* url → AudioBuffer */
    this._loading = new Map();     /* url → Promise      */
    this._unlockHandlers = [];
    this._onUnlock = [];

    this._listenForGesture();
  }

  get isRunning() { return this.supported && this.ctx.state === 'running'; }

  /* --- magistrale grup ---------------------------------------- */

  /** pobierz (lub utwórz) magistralę grupy — np. bus('bg') */
  bus(name, opts = {}) {
    if (!this.supported) return null;
    let b = this.buses.get(name);
    if (!b) {
      b = new AudioChain(this.ctx, opts);
      b.connect(this.master);
      this.buses.set(name, b);
    }
    return b;
  }

  /* --- próbki -------------------------------------------------- */

  /** wczytaj i zdekoduj próbkę (z cache); dekodowanie działa też na uśpionym kontekście */
  load(url) {
    if (!this.supported) return Promise.reject(new Error('brak Web Audio'));
    if (this._buffers.has(url)) return Promise.resolve(this._buffers.get(url));
    if (this._loading.has(url)) return this._loading.get(url);

    const p = fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error('nie udało się wczytać ' + url);
        return r.arrayBuffer();
      })
      .then((data) => this._decode(data))
      .then((buf) => {
        this._buffers.set(url, buf);
        this._loading.delete(url);
        return buf;
      })
      .catch((err) => {
        this._loading.delete(url);
        throw err;
      });

    this._loading.set(url, p);
    return p;
  }

  /** wczytaj listę próbek po kolei — pierwsza jest gotowa najszybciej */
  loadSequential(urls, onEach) {
    return urls.reduce((chain, url, i) => chain.then(() =>
      this.load(url)
        .then((buf) => { if (onEach) onEach(buf, i, url); })
        .catch(() => { /* brakująca próbka nie może wywrócić reszty */ })
    ), Promise.resolve());
  }

  getBuffer(url) { return this._buffers.get(url) || null; }

  /* --- głosy --------------------------------------------------- */

  /**
   * @param {AudioBuffer} buffer
   * @param {object} opts — { bus, volume, pan, cutoff, loop, overlap, rate, fadeIn }
   * @return {Voice}
   */
  play(buffer, opts = {}) {
    if (!this.supported || !buffer) return null;
    const voice = new Voice(this.ctx, buffer, opts);
    voice.connect(opts.bus || this.master);
    voice.start(0, opts.fadeIn || 0);
    return voice;
  }

  /* --- stan kontekstu ------------------------------------------ */

  resume() {
    if (!this.supported) return Promise.resolve(false);
    if (this.ctx.state === 'running') return Promise.resolve(true);
    return this.ctx.resume().then(() => true).catch(() => false);
  }

  suspend() {
    if (!this.supported || this.ctx.state !== 'running') return Promise.resolve();
    return this.ctx.suspend().catch(() => {});
  }

  /**
   * Wywołaj `cb`, gdy kontekst gra. Jeśli dokument ma już za sobą pierwszy
   * gest, resume() przechodzi od ręki (sticky activation) — wracając na widok
   * instalacji nie trzeba klikać ponownie. W przeciwnym razie czekamy na gest.
   */
  onUnlocked(cb) {
    if (this.isRunning) { cb(); return; }
    this._onUnlock.push(cb);
    this.resume().then((ok) => {
      if (ok) this._flushUnlock();
      else this._listenForGesture();
    });
  }

  destroy() {
    if (!this.supported) return;
    this._removeGestureListeners();
    for (const b of this.buses.values()) b.disconnect();
    this.buses.clear();
    this._onUnlock.length = 0;
    this.master.disconnect();
    if (this.ctx.state !== 'closed') this.ctx.close().catch(() => {});
  }

  /* --- wewnętrzne ---------------------------------------------- */

  /* polityka autoodtwarzania: kontekst rusza dopiero po geście użytkownika.
     Świadomie nie budujemy ekranu wprowadzającego (INTRO) — po prostu
     czekamy na pierwszą interakcję z instalacją. */
  _listenForGesture() {
    if (this.isRunning || this._unlockHandlers.length) return;
    const handler = () => {
      this.resume().then((ok) => { if (ok) this._flushUnlock(); });
    };
    UNLOCK_EVENTS.forEach((ev) => {
      window.addEventListener(ev, handler, { passive: true });
      this._unlockHandlers.push([ev, handler]);
    });
  }

  _flushUnlock() {
    this._removeGestureListeners();
    const cbs = this._onUnlock.slice();
    this._onUnlock.length = 0;
    cbs.forEach((cb) => cb());
  }

  _removeGestureListeners() {
    this._unlockHandlers.forEach(([ev, h]) => window.removeEventListener(ev, h, { passive: true }));
    this._unlockHandlers.length = 0;
  }

  /* decodeAudioData: nowa (obietnica) i stara (callbacki, Safari) sygnatura */
  _decode(data) {
    return new Promise((resolve, reject) => {
      const ret = this.ctx.decodeAudioData(data, resolve, reject);
      if (ret && typeof ret.then === 'function') ret.then(resolve, reject);
    });
  }
}

export default AudioEngine;
